'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { Search, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { LicensePlateInput } from '@/components/ui/license-plate-input';
import { api } from '@/lib/api';
import { useCartStore } from '@/stores/cart';
import { useVehicleHistoryStore } from '@/stores/vehicle-history';
import { useVehicleStore } from '@/stores/vehicle';
import { toast } from 'sonner';
import { useLocale } from '@/hooks/useLocale';
import { t } from '@/i18n/t';

interface RegistrationLookupProps {
  onComplete?: () => void;
  compact?: boolean;
  stayOnPage?: boolean;
  /** Called when reg lookup fails — typically switches the parent UI to the VIN tab. */
  onSwitchToVin?: () => void;
  /** Where this lookup widget lives — logged so we can see which entry point drives reg-searches. */
  source?: string;
}

export function RegistrationLookup({ onComplete, compact, stayOnPage, onSwitchToVin, source = 'homepage' }: RegistrationLookupProps) {
  const { locale } = useLocale();
  const [plate, setPlate] = useState('');
  const [loading, setLoading] = useState(false);
  const router = useRouter();
  const setVehicle = useCartStore((state) => state.setVehicle);
  const addEntry = useVehicleHistoryStore((state) => state.addEntry);
  const setCurrentVehicle = useVehicleStore((state) => state.setVehicle);

  const handleLookup = async () => {
    const cleanPlate = plate.replace(/\s+/g, '').toUpperCase();
    if (!cleanPlate || cleanPlate.length < 5) {
      toast.error(t(locale, 'registration.invalidPlate'));
      return;
    }

    setLoading(true);
    // Under uppslaget visar bara knappen att det laddar. Förut startades även Autofrance-laddaren här och rutan
    // fick en egen snurra — tre laddare för samma väntan (2026-10-14). Sidbytet efteråt startar laddaren ändå
    // (instrumentation-client.ts).
    try {
      const result = await api.lookupRegistration(cleanPlate, source);

      if (result.ktype && result.vehicle) {
        setCurrentVehicle(result.vehicle, cleanPlate);
        setVehicle(result.vehicle, cleanPlate, 'regnr-sok');
        addEntry(cleanPlate, result.ktype, result.vehicle);
        toast.success(t(locale, 'registration.found', { make: result.vehicle.manufacturer, model: result.vehicle.model }));
        onComplete?.();
        if (!stayOnPage) {
          router.push(`/fordon/${result.ktype}?plate=${cleanPlate}`);
        }
      } else {
        // notFound: offer VIN fallback if parent supports it.
        if (onSwitchToVin) {
          toast.error(t(locale, 'registration.notFound'), {
            action: {
              label: locale === 'sv' ? 'Prova VIN istället' : 'Try VIN instead',
              onClick: onSwitchToVin,
            },
            duration: 8000,
          });
        } else {
          toast.error(t(locale, 'registration.notFound'));
        }
      }
    } catch (error) {
      const msg = error instanceof Error ? t(locale, 'registration.error', { message: error.message }) : t(locale, 'registration.unknownError');
      if (onSwitchToVin) {
        toast.error(msg, {
          action: {
            label: locale === 'sv' ? 'Prova VIN istället' : 'Try VIN instead',
            onClick: onSwitchToVin,
          },
          duration: 8000,
        });
      } else {
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleLookup();
  };

  return (
    <form onSubmit={handleSubmit} className={compact ? "space-y-2" : "flex gap-2"}>
      <div className={compact ? "" : "flex-1"}>
        <LicensePlateInput
          value={plate}
          onChange={setPlate}
          onSubmit={handleLookup}
          disabled={loading}
          size={compact ? "sm" : "md"}
        />
      </div>
      <Button type="submit" disabled={loading} size={compact ? "default" : "lg"} className={compact ? "w-full" : "h-12"}>
        {loading ? (
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
        ) : (
          <Search className="h-4 w-4 mr-2" />
        )}
        {loading ? t(locale, 'registration.searching') : t(locale, 'registration.searchVehicle')}
      </Button>
    </form>
  );
}
